/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {FC, $Container, getProp, ContainerProps, $Sprite, CanBeSignals} from '@gongxigames/framework/core/fragments/BuilderY';
import {Store} from '@gongxigames/framework/core/fragments/store/Store';
import {Timer} from '@gongxigames/framework/core/fragments/update/Timer';
import {Circle} from 'pixi.js';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {$BtnOuter} from './BtnOuter';
import {$BtnBackground} from './BtnBackground';
import {$BtnIcon} from './BtnIcon';
import {Container as PixiContainer} from 'pixi.js';
import {Easing, Tween} from '@gongxigames/framework/core';

/**
 * state of the game
 * base - base game
 * free_spins - free spins
 * autoplay - autoplay is active
 */
export type SpinBtnMode = 'base' | 'free_spins' | 'autoplay' | 'hold_for_turbo_active';
export type SpinBtnAnimationType = 'showBtnOuter' | 'hideBtnOuter' | 'showBtnOuterTween' | 'hideBtnOuterTween';

/**
 * is Next action is spin or stop(skip)
 */
export type SpinBtnCurrentAction = 'spin' | 'stop';

export type SpinBtnBaseProps = CanBeSignals<{
    /**
     * state of the game
     * base - base game
     * free_spins - free spins
     * autoplay - autoplay is active
     */
    mode: SpinBtnMode;

    /**
     * is Next action is spin or stop(skip)
     */
    currentBtnAction: SpinBtnCurrentAction;

    /**
     * Reposition button
     */
    buttonOffset: number;

    /**
     * disable button, click event won't be emitted
     */
    disabled: boolean;

    /**
     * remaining autoplay spins
     */
    autoplayCounter: string;

    /**
     * total free spins collected
     */
    fsTotal: number;

    /**
     * current free spin index
     */
    fsCurrent: number;

    action?: () => void;
    pointerOutAction?: () => void;

    /**
     * hold spin button to run the action
     */
    holdForAction?: () => void;

    btnOuterAnimation?: SpinBtnAnimationType;
}>;

export type SpinBtnProps = ContainerProps & SpinBtnBaseProps;

const HIT_AREA = new Circle(0, 0, 116);

export const HOLD_FOR_ACTION_CALL_TIMER = 500;

export const $SpinBtn: FC<SpinBtnProps> = (props) => {
    const focused = Store.signal(false);
    const pressed = Store.signal(false);
    const enabled = Store.computed(() => !getProp(props.disabled));

    let timer: Timer | undefined;

    const setHoverTrue = () => focused.value = true;
    const setHoverFalse = () => focused.value = false;
    const setPressTrue = () => pressed.value = true;
    const setPressFalse = () => pressed.value = false;

    const runHoldForActionCallBack = () => {
        if (getProp(props.disabled)) {
            return;
        }

        if (timer && getProp(props.mode) === 'base') {
            getProp(props.holdForAction)?.();
            timer = undefined;
        }
    };

    const runClickCallBack = () => {
        timer?.stop();
        timer = undefined;
        getProp(props.action)?.();
    };

    const runCallBackPointerDown = () => {
        timer = slotStore.updateManager.value!.addTimer(() => runHoldForActionCallBack(), HOLD_FOR_ACTION_CALL_TIMER, true);
    };

    const runCallBackPointerOut = () => {
        getProp(props.pointerOutAction)?.();
    };

    const koalaLogoContainer = new PixiContainer();
    const btnOuterContainer = new PixiContainer();

    const liftUpKoalaLogoTween = () => {
        return slotStore.updateManager.value!
            .addTween(koalaLogoContainer.position)
            .to({y: 0}, 300)
            .easing(Easing.Quadratic.InOut)
            .start();
    };

    const liftUpKoalaLogo = () => {
        koalaLogoContainer.position.y = 0;
    };

    const putDownKoalaLogoTween = () => {
        return slotStore.updateManager.value!
            .addTween(koalaLogoContainer.position)
            .to({y: 40}, 300)
            .easing(Easing.Quadratic.InOut)
            .start();
    };

    const putDownKoalaLogo = () => {
        koalaLogoContainer.position.y = 40;
    };

    const showBtnOuterTween = () => {
        btnOuterContainer.visible = true;

        return slotStore.updateManager.value!
            .addTween(btnOuterContainer.scale)
            .to({x: 1, y: 1}, 50)
            .easing(Easing.Quadratic.In)
            .onComplete(() => {
                slotStore.updateManager.value!
                    .addTween(btnOuterContainer)
                    .to({rotation: Math.PI / 10}, 50)
                    .easing(Easing.Quadratic.InOut).onComplete(() => {
                    slotStore.updateManager.value!
                        .addTween(btnOuterContainer)
                        .to({rotation: -Math.PI / 10}, 100)
                        .easing(Easing.Quadratic.InOut).onComplete(() => {
                        slotStore.updateManager.value!
                            .addTween(btnOuterContainer)
                            .to({rotation: 0}, 50)
                            .easing(Easing.Quadratic.InOut)
                            .start();
                        })
                        .start();
                    })
                    .start();
            })
            .start();
    };

    const hideBtnOuterTween = () => {
        return slotStore.updateManager.value!
            .addTween(btnOuterContainer.scale)
            .to({x: 0.6, y: .6}, 50)
            .easing(Easing.Quadratic.In)
            .start().onComplete(() => {
                btnOuterContainer.visible = false;
            });
    };

    const showBtnOuter = () => {
        btnOuterContainer.visible = true;
        btnOuterContainer.scale.set(1);
    };

    const hideBtnOuter = () => {
        btnOuterContainer.visible = false;
        btnOuterContainer.scale.set(0.6);
    };

    const tweens: Tween[] = [];
    const offAnimation = Store.effect(() => {
        const animation = getProp(props.btnOuterAnimation);

        tweens.forEach((tween) => tween.stop());
        tweens.length = 0;
        switch (animation) {
            case 'hideBtnOuterTween':
                tweens.push(putDownKoalaLogoTween());
                tweens.push(hideBtnOuterTween());
                break;
            case 'hideBtnOuter':
                putDownKoalaLogo();
                hideBtnOuter();
                break;
            case 'showBtnOuter':
                liftUpKoalaLogo();
                showBtnOuter();
                break;
            case 'showBtnOuterTween':
                tweens.push(liftUpKoalaLogoTween());
                tweens.push(showBtnOuterTween());
                break;
            default:
                break;
        }
    });

    const offSkipHold = Store.effect(() => {
        const shouldSkipHold = getProp(props.disabled);

        if (shouldSkipHold) {
            timer?.stop();
            timer = undefined;
            runCallBackPointerOut();
        }
    });

    const events = {
        pointerover: setHoverTrue,
        pointerout: [setHoverFalse, runCallBackPointerOut],
        pointerdown: [setPressTrue, runCallBackPointerDown],
        pointerup: [setPressFalse, runClickCallBack],
        pointerupoutside: [setPressFalse, runCallBackPointerOut],
        destroyed: [offSkipHold, offAnimation],
    };

    return <$Container
        name='spin_button'
        {...props}
        interactive={enabled}
        buttonMode={enabled}
        events={events}
        hitArea={HIT_AREA}
    >
        <$Container name='koala_logo_ui_animation' instance={koalaLogoContainer}>
            <$Sprite name='koala' y={-175} anchor={0.5} texture='koala_logo_ui'/>
        </$Container>

        <$Container name='btn_outer_animation' instance={btnOuterContainer}>
            <$BtnOuter mode={props.mode}/>
        </$Container>

        <$BtnBackground disabled={props.disabled} pressed={pressed}/>
        <$BtnIcon
            disabled={props.disabled}
            focused={focused}
            currentBtnAction={props.currentBtnAction}
            mode={props.mode}
            autoplayCounter={props.autoplayCounter}
            fsCurrent={props.fsCurrent}
            fsTotal={props.fsTotal}
        />
    </$Container>;
};